import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch } from 'react-icons/fi';
import { useWallet } from '../context/WalletContext';
import { useChain } from '../context/ChainContext';
import { MAIN_ASSETS, POPULAR_BEP20_TOKENS, MULTI_CHAIN_CONFIG } from '../constants/tokens';
import TokenIcon from './TokenIcon';
import '../styles/TokenSelector.css';
import { useState as useReactState } from 'react';

const TokenSelector = ({ isOpen, onClose, onSelect, selectedToken }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { tokenBalances, balance, customTokens, tokenPrices, detectedTokens, chainBalances } = useWallet();
  const { selectedChain, isTestnet } = useChain();
  const [expandedGroups, setExpandedGroups] = useState({
    'NATIVE TOKENS': true,
    'STABLECOINS': true,
    'PLATFORM TOKENS': true,
    'CUSTOM TOKENS': true
  });

  // Calculate USD value with memoization
  const calculateUSDValue = useCallback((token, balance) => {
    if (!token || balance === undefined) return '0.00';
    try {
      const price = token.symbol === 'USDT' || token.symbol === 'USDC' ? 1 :
                    token.isPlatformToken ? tokenPrices?.TGR?.price || 0 :
                    token.symbol === 'TON' ? tokenPrices?.TON?.price || 0 :
                    token.symbol === 'BNB' ? tokenPrices?.BNB?.price || 0 :
                    tokenPrices?.[token.symbol]?.price || 0;

      const actualBalance = parseFloat(balance);
      if (isNaN(actualBalance)) return '0.00';
      
      const usdValue = actualBalance * price;
      return isNaN(usdValue) ? '0.00' : usdValue.toFixed(2);
    } catch (error) {
      console.warn('Error calculating USD value:', error);
      return '0.00';
    }
  }, [tokenPrices]);

  // Format balance with memoization
  const formatBalance = useCallback((balance, decimals = 4) => {
    if (!balance) return '0.0000';
    try {
      const numBalance = parseFloat(balance);
      return isNaN(numBalance) ? '0.0000' : numBalance.toFixed(decimals);
    } catch {
      return '0.0000';
    }
  }, []);

  // For multi-chain token selection
  const [multiChainPrompt, setMultiChainPrompt] = useReactState({ open: false, token: null });

  // Build a flat list of all tokens (native, Jettons, BEP20, custom, detected)
  const allTokens = useMemo(() => {
    const network = isTestnet ? 'testnet' : 'mainnet';
    let tokens = [];

    // Native tokens
    ['TON', 'BNB'].forEach(symbol => {
      const token = MAIN_ASSETS[symbol];
      if (token) {
        tokens.push({
          ...token,
          isNative: true,
          chain: symbol,
          address: token.address || '',
          balance: symbol === 'TON' ? balance : (chainBalances && chainBalances.BNB ? chainBalances.BNB : '0'),
        });
      }
    });

    // TON Jettons (MAIN_ASSETS, except TON/BNB)
    Object.entries(MAIN_ASSETS).forEach(([symbol, token]) => {
      if (token.chain === 'TON' && symbol !== 'TON') {
        const tokenAddress = isTestnet ? token.testnetAddress : token.mainnetAddress;
        tokens.push({
          ...token,
          address: tokenAddress,
          chain: 'TON',
          balance: tokenBalances[symbol]?.balance || tokenBalances[symbol]?.data?.balance || '0',
        });
      }
    });

    // BNB BEP20 tokens
    (POPULAR_BEP20_TOKENS[network] || []).forEach(token => {
      tokens.push({
        ...token,
        chain: 'BNB',
        address: token.address,
        balance: tokenBalances[token.address]?.balance || tokenBalances[token.address]?.data?.balance || '0',
      });
    });

    // Custom tokens
    Object.values(customTokens).forEach(token => {
      const tokenNetworkAddress = token[`${network}Address`] || token.address;
      tokens.push({
        ...token,
        address: tokenNetworkAddress,
        chain: token.chain || '',
        balance: tokenBalances[tokenNetworkAddress]?.balance || '0',
      });
    });

    // Detected tokens
    if (typeof detectedTokens === 'object' && detectedTokens !== null) {
      Object.values(detectedTokens).forEach(token => {
        const tokenNetworkAddress = token[`${network}Address`] || token.address;
        tokens.push({
          ...token,
          address: tokenNetworkAddress,
          chain: token.chain || '',
          balance: tokenBalances[tokenNetworkAddress]?.balance || '0',
        });
      });
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      tokens = tokens.filter(token =>
        (token.name && token.name.toLowerCase().includes(query)) ||
        (token.symbol && token.symbol.toLowerCase().includes(query))
      );
    }

    // Remove duplicates by address+symbol
    const seen = new Set();
    tokens = tokens.filter(token => {
      const key = `${token.address}-${token.symbol}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return tokens;
  }, [searchQuery, customTokens, detectedTokens, tokenBalances, balance, isTestnet, chainBalances]);

  // Helper: get supported chains for a token
  const getSupportedChains = (token) => {
    if (token.isMultiChain && Array.isArray(token.supportedChains)) {
      return token.supportedChains;
    }
    return [token.chain];
  };

  const renderTokenItem = useCallback((token) => {
    const isSelected = selectedToken?.symbol === token.symbol && selectedToken?.address === token.address;
    const supportedChains = getSupportedChains(token);
    const isMultiChain = supportedChains.length > 1;
    
    // Check if this is a multi-chain stablecoin (USDT, USDC)
    const isMultiChainStablecoin = isMultiChain && ['USDT', 'USDC'].includes(token.symbol);
    
    // Always use PNG icon if available
    const iconPng = `/assets/tokens/${token.symbol?.toLowerCase()}.png`;
    const tokenWithIcon = { ...token, image: iconPng };
    
    return (
      <motion.button
        key={`${token.symbol}-${token.address}`}
        className={`token-item ${isSelected ? 'selected' : ''} ${isMultiChainStablecoin ? 'multi-chain-stablecoin' : ''}`}
        onClick={() => {
          if (isMultiChain) {
            console.log(`🔗 Multi-chain token selected: ${token.symbol}`, {
              supportedChains,
              isMultiChainStablecoin,
              token
            });
            setMultiChainPrompt({ open: true, token });
          } else {
            console.log(`🔗 Single-chain token selected: ${token.symbol} on ${supportedChains[0]}`, {
              token,
              selectedChain: supportedChains[0]
            });
            onSelect({ ...token, selectedChain: supportedChains[0] });
          }
        }}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="token-left-section">
          <div className="token-icon-wrapper">
            <TokenIcon token={tokenWithIcon} size={22} />
          </div>
          <div className="token-info">
            <div className="token-symbol">
              {token.symbol}
              {isMultiChainStablecoin && (
                <span className="multi-chain-indicator" title="Available on multiple chains">
                  🔗
                </span>
              )}
            </div>
            <span className="token-name">{token.name}</span>
            {isMultiChainStablecoin && (
              <span className="multi-chain-hint">
                Available on {supportedChains.join(' & ')}
              </span>
            )}
          </div>
        </div>
        <div className="balance-info">
          {isMultiChain ? (
            <span className="multi-chain-badge" title={`Available on ${supportedChains.join(', ')}`}>
              Multi
            </span>
          ) : null}
          <span className={`chain-badge ${token.chain?.toLowerCase()}`}>{token.chain}</span>
          <span className="balance">{formatBalance(token.balance)}</span>
          <span className="usd-value">${calculateUSDValue(token, token.balance)}</span>
        </div>
      </motion.button>
    );
  }, [selectedToken, formatBalance, calculateUSDValue, onSelect]);

  // Multi-chain selection modal
  const renderMultiChainPrompt = () => {
    if (!multiChainPrompt.open || !multiChainPrompt.token) return null;
    const token = multiChainPrompt.token;
    const supportedChains = getSupportedChains(token);
    
    console.log(`🔗 Multi-chain selection modal opened for ${token.symbol}`, {
      supportedChains,
      token
    });
    
    return (
      <div className="multi-chain-modal">
        <div className="multi-chain-modal-content">
          <h4>Select Chain for {token.symbol}</h4>
          <p className="multi-chain-description">
            {token.symbol} is available on multiple chains. Choose which network you want to use:
          </p>
          <div className="multi-chain-options">
            {supportedChains.map(chain => {
              const networkType = isTestnet ? 'testnet' : 'mainnet';
              const chainConfig = MULTI_CHAIN_CONFIG[token.symbol]?.[chain];
              const tokenAddress = chainConfig?.[`${networkType}Address`];
              
              console.log(`🔗 Chain option: ${chain}`, {
                tokenAddress,
                networkType
              });
              
              return (
                <button
                  key={chain}
                  className={`chain-select-btn chain-badge ${chain.toLowerCase()}`}
                  onClick={() => {
                    console.log(`🔗 User selected ${chain} for ${token.symbol}`, {
                      tokenAddress,
                      selectedChain: chain
                    });
                    setMultiChainPrompt({ open: false, token: null });
                    onSelect({ 
                      ...token, 
                      selectedChain: chain,
                      chain: chain,
                      address: tokenAddress,
                      [`${networkType}Address`]: tokenAddress
                    });
                  }}
                >
                  <div className="chain-select-content">
                    <span className="chain-name">{chain}</span>
                    <span className="chain-address">
                      {tokenAddress ? `${tokenAddress.slice(0, 6)}...${tokenAddress.slice(-4)}` : 'No address'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <button 
            className="close-multi-chain-modal" 
            onClick={() => {
              console.log('🔗 Multi-chain selection cancelled');
              setMultiChainPrompt({ open: false, token: null });
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="token-selector-modal">
          <motion.div
            className="token-selector-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="token-selector-header">
              <h3>Select Token ({isTestnet ? 'Testnet' : 'Mainnet'})</h3>
              <button onClick={onClose}>
                <FiX size={18} />
              </button>
            </div>

            <div className="token-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Search by name or symbol"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="token-list">
              {allTokens.length > 0 ? (
                allTokens.map(renderTokenItem)
              ) : (
                <div className="no-tokens-message">No tokens found</div>
              )}
            </div>
            {renderMultiChainPrompt()}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TokenSelector; 